import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { COMPANY, SERVICES } from '../data/company';

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location.pathname, location.hash]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isServicesActive = location.pathname.startsWith('/ydelser');

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-sand-border shadow-xs">
      {/* Top Contact Bar */}
      <div className="bg-forest text-white text-xs sm:text-sm py-2 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-4 sm:gap-6">
            <a 
              href={`tel:${COMPANY.phone}`} 
              className="flex items-center gap-2 hover:text-sky-200 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-sky-300" aria-hidden="true" />
              <span className="font-medium whitespace-nowrap">{COMPANY.phoneFormatted}</span>
            </a>
            <a 
              href={`mailto:${COMPANY.email}`} 
              className="hidden sm:flex items-center gap-2 hover:text-sky-200 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-sky-300" aria-hidden="true" />
              <span className="whitespace-nowrap">{COMPANY.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-2 text-xs text-sand/90">
            <i className="fa-solid fa-location-dot text-sky-300" aria-hidden="true" />
            <span>Lokal service i {COMPANY.city} & omegn</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-24 sm:h-28 flex items-center justify-between">
        {/* Brand Logo / Name */}
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-forest text-white flex items-center justify-center font-bold text-xl group-hover:scale-105 transition-all overflow-hidden">
            <img 
              src="https://assets.auxodev.dk/assets/3afae24a-c90c-4b91-94fe-484783460244/img-1851_1.jpeg" 
              alt={COMPANY.name} 
              className="w-full h-full object-cover object-center"
            />
          </div>
        </Link>

        {/* Desktop Nav - ONLY strictly visible on >= 1200px */}
        <nav className="hidden min-[1200px]:flex items-center gap-1 font-medium text-charcoal">
          <Link
            to="/"
            className={`px-4 py-2 rounded-md transition-colors ${
              isActive('/') && !isServicesActive && location.pathname !== '/om-os' && location.pathname !== '/galleri' && location.pathname !== '/kontakt'
                ? 'bg-sand text-forest font-semibold'
                : 'hover:bg-sand/60 hover:text-forest'
            }`}
          >
            Forside
          </Link>

          <Link
            to="/om-os"
            className={`px-4 py-2 rounded-md transition-colors ${
              isActive('/om-os')
                ? 'bg-sand text-forest font-semibold'
                : 'hover:bg-sand/60 hover:text-forest'
            }`}
          >
            Om Altmuligmanden
          </Link>

          {/* Ydelser Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
              onMouseEnter={() => setServicesDropdownOpen(true)}
              className={`px-4 py-2 rounded-md transition-colors flex items-center gap-2 cursor-pointer ${
                isServicesActive
                  ? 'bg-sand text-forest font-semibold'
                  : 'hover:bg-sand/60 hover:text-forest'
              }`}
              aria-expanded={servicesDropdownOpen}
            >
              <span>Ydelser</span>
              <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${servicesDropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>

            {/* Dropdown Menu */}
            {servicesDropdownOpen && (
              <div 
                className="absolute top-full left-0 mt-1 w-72 bg-white rounded-xl shadow-xl border border-sand-border py-2 z-50"
                onMouseLeave={() => setServicesDropdownOpen(false)}
              >
                <div className="px-4 py-2 border-b border-sand-border mb-1">
                  <Link
                    to="/ydelser"
                    className="text-xs font-bold uppercase tracking-wider text-copper hover:underline block"
                  >
                    Se alle ydelser <i className="fa-solid fa-arrow-right ml-1" aria-hidden="true" />
                  </Link>
                </div>
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/ydelser/${s.slug}`}
                    className={`flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-sand/70 transition-colors ${
                      location.pathname === `/ydelser/${s.slug}` ? 'bg-sand text-forest font-bold' : 'text-charcoal'
                    }`}
                  >
                    <div className="w-7 h-7 rounded-md bg-forest/10 text-forest flex items-center justify-center text-xs">
                      <i className={s.icon} aria-hidden="true" />
                    </div>
                    <span>{s.title}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Galleri Link */}
          <Link
            to="/galleri"
            className={`px-4 py-2 rounded-md transition-colors ${
              isActive('/galleri')
                ? 'bg-sand text-forest font-semibold'
                : 'hover:bg-sand/60 hover:text-forest'
            }`}
          >
            Galleri
          </Link>

          <Link
            to="/kontakt"
            className={`px-4 py-2 rounded-md transition-colors ${
              isActive('/kontakt')
                ? 'bg-sand text-forest font-semibold'
                : 'hover:bg-sand/60 hover:text-forest'
            }`}
          >
            Kontakt
          </Link>
        </nav>

        {/* Desktop CTA Button */}
        <div className="hidden min-[1200px]:flex items-center gap-3">
          <Link
            to="/kontakt"
            className="bg-copper hover:bg-copper-hover text-white font-semibold px-5 py-2.5 rounded-lg shadow-sm transition-all duration-200 transform hover:-translate-y-0.5 flex items-center gap-2 cursor-pointer"
          >
            <i className="fa-solid fa-paper-plane" aria-hidden="true" />
            <span>Få et uforpligtende tilbud</span>
          </Link>
        </div>

        {/* Hamburger Toggle - ACTIVE ONLY below 1200px */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="min-[1200px]:hidden p-2.5 rounded-lg text-charcoal hover:bg-sand transition-colors cursor-pointer"
          aria-label="Åbn menu"
          aria-expanded={mobileMenuOpen}
        >
          <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} aria-hidden="true" />
        </button>
      </div>

      {/* Mobile/Tablet Menu Drawer - Active ONLY below 1200px */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-sand-border px-4 pt-3 pb-6 space-y-2 shadow-lg max-h-[85vh] overflow-y-auto">
          <Link
            to="/"
            className={`block px-4 py-3 rounded-lg font-medium ${
              location.pathname === '/' ? 'bg-sand text-forest font-bold' : 'text-charcoal hover:bg-sand/50'
            }`}
          >
            <i className="fa-solid fa-house mr-3 text-forest" aria-hidden="true" />
            Forside
          </Link>

          <Link
            to="/om-os"
            className={`block px-4 py-3 rounded-lg font-medium ${
              location.pathname === '/om-os' ? 'bg-sand text-forest font-bold' : 'text-charcoal hover:bg-sand/50'
            }`}
          >
            <i className="fa-solid fa-user-check mr-3 text-forest" aria-hidden="true" />
            Om Altmuligmanden
          </Link>

          {/* Mobile Services Accordion */}
          <div>
            <button
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium text-charcoal hover:bg-sand/50 cursor-pointer"
            >
              <div className="flex items-center">
                <i className="fa-solid fa-toolbox mr-3 text-forest" aria-hidden="true" />
                <span>Ydelser</span>
              </div>
              <i className={`fa-solid fa-chevron-down text-xs transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>

            {mobileServicesOpen && (
              <div className="pl-6 pr-2 py-1 space-y-1 bg-sand/30 rounded-lg my-1">
                <Link
                  to="/ydelser"
                  className="block px-4 py-2 text-sm font-bold text-copper"
                >
                  Oversigt over alle ydelser →
                </Link>
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/ydelser/${s.slug}`}
                    className={`block px-4 py-2.5 text-sm rounded-md ${
                      location.pathname === `/ydelser/${s.slug}` ? 'bg-sand text-forest font-bold' : 'text-charcoal hover:bg-sand/60'
                    }`}
                  >
                    <i className={`${s.icon} mr-2 text-forest/70`} aria-hidden="true" />
                    {s.title}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Galleri Link */}
          <Link
            to="/galleri"
            className={`block px-4 py-3 rounded-lg font-medium ${
              location.pathname === '/galleri' ? 'bg-sand text-forest font-bold' : 'text-charcoal hover:bg-sand/50'
            }`}
          >
            <i className="fa-solid fa-images mr-3 text-forest" aria-hidden="true" />
            Galleri
          </Link>

          <Link
            to="/kontakt"
            className={`block px-4 py-3 rounded-lg font-medium ${
              location.pathname === '/kontakt' ? 'bg-sand text-forest font-bold' : 'text-charcoal hover:bg-sand/50'
            }`}
          >
            <i className="fa-solid fa-envelope-open-text mr-3 text-forest" aria-hidden="true" />
            Kontakt
          </Link>

          <div className="pt-4 border-t border-sand-border space-y-3">
            <a
              href={`tel:${COMPANY.phone}`}
              className="w-full bg-forest text-white font-medium py-3 rounded-xl flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone text-white" aria-hidden="true" />
              <span className="whitespace-nowrap">Ring: {COMPANY.phoneFormatted}</span>
            </a>

            <Link
              to="/kontakt"
              className="w-full bg-copper text-white font-medium py-3 rounded-xl flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-paper-plane" aria-hidden="true" />
              <span>Få et uforpligtende tilbud</span>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
